import React, {useEffect, useRef, useState} from "react";
import Lottie from "lottie-react";
import ClickAnimation from "../assets/json/ClickAnimation.json";

type Area = {
  shape: string;
  coords: string;
  alt: string;
  color?: string;
  className?: string;
};

type ResponsiveImageMapProps = {
  src: string;
  alt: string;
  mapName: string;
  areas: Area[];
};

const ResponsiveImageMapRecruitment: React.FC<ResponsiveImageMapProps> = ({
  src,
  alt,
  mapName,
  areas,
}) => {
  const imgRef = useRef<HTMLImageElement | null>(null);
  const [scaledAreas, setScaledAreas] = useState<Area[]>([]);
  const [highlightedIndex, setHighlightedIndex] = useState<number | null>(null); // Track highlighted area index

  useEffect(() => {
    const resizeMap = () => {
      if (!imgRef.current) return;

      const img = imgRef.current;
      if (!img.naturalWidth || !img.naturalHeight) return;

      const originalWidth = img.naturalWidth;
      const originalHeight = img.naturalHeight;

      const currentWidth = img.offsetWidth;
      const currentHeight = img.offsetHeight;

      const xRatio = currentWidth / originalWidth;
      const yRatio = currentHeight / originalHeight;

      const updatedAreas = areas.map((area) => {
        const coords = area.coords.split(",").map(Number);
        const scaledCoords = coords.map((coord, index) =>
          index % 2 === 0 ? coord * xRatio : coord * yRatio
        );
        return {...area, coords: scaledCoords.join(",")};
      });

      setScaledAreas(updatedAreas);
    };

    const handleResize = () => resizeMap();

    window.addEventListener("resize", handleResize);

    const img = imgRef.current;
    if (img && img.complete) {
      resizeMap();
    } else {
      img?.addEventListener("load", resizeMap);
    }

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [areas]);

  const renderBackgrounds = () => {
    return scaledAreas.map((area, index) => {
      const coords = area.coords.split(",").map(Number);
      let style: React.CSSProperties = {};

      if (area.shape === "rect") {
        style = {
          left: `${coords[0]}px`,
          top: `${coords[1]}px`,
          width: `${coords[2] - coords[0]}px`,
          height: `${coords[3] - coords[1]}px`,
        };
      } else if (area.shape === "circle") {
        style = {
          left: `${coords[0] - coords[2]}px`,
          top: `${coords[1] - coords[2]}px`,
          width: `${coords[2] * 2}px`,
          height: `${coords[2] * 2}px`,
          borderRadius: "50%",
        };
      }

      const isHighlighted = index === highlightedIndex;

      return (
        <div
          key={index}
          className={`parent-modal-bubble-recruitment ${area.alt}-hover-blk`}
          onMouseEnter={() => {
            const cursorPointer = document.getElementById(
              `cursorPointer-${index}`
            );
            if (cursorPointer) {
              cursorPointer.style.display = "none";
            }
          }}
          style={{
            ...style,
            position: "absolute",
            backgroundColor: isHighlighted
              ? "rgba(0, 255, 0, 0.5)"
              : area.color || "rgba(255, 0, 0, 0.5)",
            cursor: "pointer",
          }}>
          <Lottie
            id={`cursorPointer-${index}`}
            animationData={ClickAnimation}
            className="click-animation-blk-recruitment"
          />
          {area.alt === "process1" && (
            <div
              className={`modal-bubble-dna ${area.alt.toLocaleLowerCase()}-bubble-blk`}>
              <h4>PRE-SCREEN</h4>
              <ul>
                <li>
                  <p>Minimum requirements</p>
                </li>
                <li>
                  <p>Salary expectations</p>
                </li>
                <li>
                  <p>Years of experience</p>
                </li>
                <li>
                  <p>Availability</p>
                </li>
              </ul>
            </div>
          )}

          {area.alt === "process2" && (
            <div
              className={`modal-bubble-dna ${area.alt.toLocaleLowerCase()}-bubble-blk`}>
              <h4>RESUME SCREEN</h4>
              <ul>
                <li>
                  <p>Skills and qualifications</p>
                </li>
                <li>
                  <p>Relevant work experience</p>
                </li>
                <li>
                  <p>Educational background</p>
                </li>
                <li>
                  <p>Job-specific criteria</p>
                </li>
              </ul>
            </div>
          )}

          {area.alt === "process3" && (
            <div
              className={`modal-bubble-dna ${area.alt.toLocaleLowerCase()}-bubble-blk`}>
              <h4>QUALIFYING INTERVIEW</h4>
              <ul>
                <li>
                  <p>Problem-solving approach</p>
                </li>
                <li>
                  <p>Motivation and passion</p>
                </li>
                <li>
                  <p>Technical competencies verification</p>
                </li>
                <li>
                  <p>Salary expectations clarification</p>
                </li>
              </ul>
            </div>
          )}

          {area.alt === "process4" && (
            <div
              className={`modal-bubble-dna ${area.alt.toLocaleLowerCase()}-bubble-blk`}>
              <h4>COGNITIVE ASSESSMENT</h4>
              <ul>
                <li>
                  <p>Communication skills</p>
                </li>
                <li>
                  <p>Comprehension</p>
                </li>
                <li>
                  <p>Attention to detail</p>
                </li>
                <li>
                  <p>Reasoning ability</p>
                </li>
              </ul>
            </div>
          )}

          {area.alt === "process5" && (
            <div
              className={`modal-bubble-dna ${area.alt.toLocaleLowerCase()}-bubble-blk`}>
              <h4>CULTURE FIT INTERVIEW</h4>
              <ul>
                <li>
                  <p>iCXeed DNA and values alignment</p>
                </li>
                <li>
                  <p>Client culture fit</p>
                </li>
                <li>
                  <p>Learning agility and innovation quotient</p>
                </li>
                <li>
                  <p>Long-term potential</p>
                </li>
              </ul>
            </div>
          )}
          {area.alt === "process6" && (
            <div
              className={`modal-bubble-dna ${area.alt.toLocaleLowerCase()}-bubble-blk`}>
              <h4>CLIENT VALIDATION</h4>
              <ul>
                <li>
                  <p>Client persona fit</p>
                </li>
              </ul>
            </div>
          )}
        </div>
      );
    });
  };

  return (
    <div style={{position: "relative"}}>
      <img
        ref={imgRef}
        src={src}
        useMap={`#${mapName}`}
        alt={alt}
        style={{maxWidth: "100%", height: "auto", display: "block"}}
      />
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
        }}>
        {renderBackgrounds()}
      </div>
    </div>
  );
};

export default ResponsiveImageMapRecruitment;
